import { ParcelamosTudo3DSV2 } from "@parcelamostudo-tech/lib-3ds-client";
import { useWhitelabel } from "hooks";
import usePayment, {
  ICard,
  ICreditCard,
  PaymentMethods,
} from "hooks/usePayment";
import { IInstallments } from "models";
import { useEffect, useRef, useState } from "react";
import { useMutation } from "react-query";
import { toast } from "react-toastify";
import {
  Card,
  CreateChargeRequestDTO,
  CreateChargeResponse,
  CreateCheckoutOrderRequest,
  ThreeDs,
} from "services/PublicBff/type";
import PublicBffService from "../../../services/PublicBff";
import { useNavigate } from "react-router-dom";
import {
  ApiResponseError,
  EnumErrorCode,
  getApiErrorMessages,
} from "services/errors";
import { useFlowResume } from "./FlowResume";
import { ChargeIdStatusType } from "views/Pix/providers/dto/create-checkout.dto";
import { mapInstallments } from "../utils/constants";
import { EnumPaymentMethod } from "../components/FormPayment/types";
import { EnumStatusPayment as EnumStatusPaymentType } from "hooks/usePayment";
import {
  EnumStatus3DS,
  EnumStatusPayment,
  TOAST_3DS_AUTO_CLOSE,
  TOAST_3DS_ID,
} from "../utils/toastStatus";
import { isCard } from "../utils";
import { normalizeCardYear } from "utils/helper";
import { isEnvironmentTest, X_TOKEN_AUTOMATION } from "config/env";

function buildCardData(method: ICard | ICreditCard) {
  const [month, year] = method.due_date.split("/");

  return {
    exp_month: month,
    exp_year: year,
    holder_name: method.name,
    number: method.number,
    security_code: method.cvv,
  };
}

function getBrowserInfoHeaders() {
  return {
    browser_color_depth: String(window.screen.colorDepth),
    browser_java_enabled: navigator.javaEnabled() ? "true" : "false",
    browser_javascript_enabled: "true",
    browser_language: navigator.language,
    browser_screen_height: String(window.screen.height),
    browser_screen_width: String(window.screen.width),
    browser_tz: String(new Date().getTimezoneOffset()),
    browser_user_agent: navigator.userAgent,
  };
}

function getChargeOptionId(
  payment: PaymentMethods & { index: number },
  installments: IInstallments
) {
  const isMultiple = !!installments?.multiple?.options;

  if (isMultiple) {
    return installments?.multiple?.options[payment.index]?.id_charge_option;
  }

  const installmentsNumber =
    payment.typeMethod === EnumPaymentMethod.CREDITO
      ? (payment as ICreditCard).installments - 1
      : 0;

  return mapInstallments[payment.typeMethod](installments, installmentsNumber)
    ?.id_charge_option;
}

function getChargeOptionAmount(
  payment: PaymentMethods & { index: number },
  installments: IInstallments
) {
  const isMultiple = !!installments?.multiple?.options;

  if (isMultiple) {
    return installments?.multiple?.options[payment.index]?.total_amount;
  }

  const installmentsNumber =
    payment.typeMethod === EnumPaymentMethod.CREDITO
      ? (payment as ICreditCard).installments - 1
      : 0;

  return mapInstallments[payment.typeMethod](installments, installmentsNumber)
    ?.total_amount;
}

interface ThreeDSValidationParams {
  tokenRecaptcha: string | null;
  payment?: (PaymentMethods & { index: number }) | null;
  onExpiredRecaptcha?: () => void;
  setInfoPix?: (data: CreateChargeResponse) => void;
}

export function useCheckout({
  tokenRecaptcha,
  onExpiredRecaptcha,
  setInfoPix,
}: ThreeDSValidationParams) {
  const { services, userIdWhitelabel } = useWhitelabel();
  const {
    client,
    installments,
    setStatusMethod,
    setChargeIdMethod,
    paymentMethods,
    rulesFromQuery,
  } = usePayment();
  const { getInstallments } = useFlowResume({});
  const navigate = useNavigate();
  const paymentSelected = useRef<(PaymentMethods & { index: number }) | null>(
    null
  );
  const idCheckout = useRef<string | null>(null);

  const [isLoading, setLoading] = useState(false);

  async function handleMutationError(
    error: unknown,
    options: {
      checkExpired?: EnumErrorCode;
      setStatusError?: boolean;
    } = {}
  ) {
    const responseError = (error as any).response.data as ApiResponseError;

    if (options.setStatusError) {
      setStatusMethod(
        EnumStatusPaymentType.ERROR,
        paymentSelected.current!.index
      );
    }

    if (
      options.checkExpired &&
      responseError.error === options.checkExpired
    ) {
      await getInstallments();
      toast.update(TOAST_3DS_ID, {
        render: "Tempo para pagamento expirou, tente novamente!",
        autoClose: TOAST_3DS_AUTO_CLOSE,
        type: "error",
        isLoading: false,
      });
      return;
    }

    if (responseError.message === EnumErrorCode.INVALID_RECAPTCHA_TOKEN) {
      onExpiredRecaptcha?.();
      toast.update(TOAST_3DS_ID, {
        render: "Token de segurança expirou, tente novamente!",
        autoClose: TOAST_3DS_AUTO_CLOSE,
        type: "error",
        isLoading: false,
      });
      return;
    }

    const messageError = getApiErrorMessages(responseError);
    toast.update(TOAST_3DS_ID, {
      render: messageError,
      autoClose: TOAST_3DS_AUTO_CLOSE,
      type: "error",
      isLoading: false,
    });
  }

  const { mutateAsync: mutateCreateCheckout } = useMutation({
    mutationFn: PublicBffService.createCheckout,
    onSuccess(data) {
      idCheckout.current = data?.id_checkout;

      data?.charges?.map((charge, index) => {
        setChargeIdMethod(charge.id_charge, index);
      });
    },
    async onError(error) {
      await handleMutationError(error, {
        checkExpired: EnumErrorCode.CHECKOUT_CHARGE_OPTIONS_EXPIRED,
      });
    },
    retry: false,
  });

  const { mutateAsync: mutateCreateCharge } = useMutation({
    mutationFn: PublicBffService.createCharge,
    onSuccess(data) {
      idCheckout.current = data?.checkout_id;

      if (data.type === "PIX") {
        setInfoPix?.(data as CreateChargeResponse);
        return;
      }

      if (data.id_status === ChargeIdStatusType.CHARGE_DONE) {
        setStatusMethod(
          EnumStatusPaymentType.SUCCESS,
          paymentSelected.current!.index
        );
        toast.update(TOAST_3DS_ID, {
          render: EnumStatus3DS.CHARGE_SUCCESS,
          autoClose: TOAST_3DS_AUTO_CLOSE,
          type: "success",
          isLoading: false,
        });
        return;
      }

      if (data.id_status === ChargeIdStatusType.CHARGE_DENIED) {
        toast.update(TOAST_3DS_ID, {
          render: EnumStatusPayment.CHARGE_DENIED,
          autoClose: TOAST_3DS_AUTO_CLOSE,
          type: "error",
          isLoading: false,
        });
        setStatusMethod(
          EnumStatusPaymentType.ERROR,
          paymentSelected.current!.index
        );
      }
    },
    async onError(error) {
      await handleMutationError(error, {
        checkExpired: EnumErrorCode.CHARGE_CHARGE_OPTIONS_EXPIRED,
        setStatusError: true,
      });
    },
    retry: false,
  });

  async function authenticateThreeDS(payment: PaymentMethods & { index: number }): Promise<ThreeDs> {
    const { public_key } = await PublicBffService.getPublicKey(userIdWhitelabel);
    const parcelamos3DS = new ParcelamosTudo3DSV2(public_key);

    const cardData = buildCardData(payment as ICard | ICreditCard);

    const executeReq = {
      amount: Math.round(getChargeOptionAmount(payment, installments) * 100),
      currency: "BRL",
      card: {
        number: cardData.number.replace(/\s/g, ""),
        exp_month: cardData.exp_month,
        exp_year: normalizeCardYear(cardData.exp_year),
      },
      user: {
        document: client.document?.replace(/\D/g, ""),
        email: client.email,
        first_name: client.name.split(" ")[0],
        last_name: client.name.split(" ").slice(1).join(" "),
        phone_number: client.phone?.replace(/\D/g, ""),
        address: client.address?.address,
        city: client.address?.city,
        state: client.address?.state,
        postal_code: client.address?.zip_code?.replace(/\D/g, ""),
        country: "BR",
      },
    };

    const result = await parcelamos3DS.execute(executeReq);

    if (!result.success) {
      throw new Error(result.message || "Erro na autenticação 3DS");
    }

    return {
      id: result.id_three_ds,
      provider: "CYBERSOURCE",
    };
  }

  async function createCharge(
    payment: PaymentMethods & { index: number },
    newToken?: string
  ) {
    if (!tokenRecaptcha) return;

    paymentSelected.current = payment;
    const token = newToken || tokenRecaptcha;
    setLoading(true);
    setStatusMethod(EnumStatusPaymentType.STARTED, payment.index);

    const toastText =
      services.run_three_ds && payment.typeMethod !== EnumPaymentMethod.PIX
        ? EnumStatus3DS.STARTED
        : EnumStatusPayment.STARTED;

    toast.loading(toastText, { toastId: TOAST_3DS_ID });

    const clientPayload = {
      id_client: client.id_client,
      name: client.name,
      email: client.email,
      document: client.document,
      phone: client.phone,
    };

    const payloadCharge: CreateChargeRequestDTO = {
      client: clientPayload,
      id_charge_option: getChargeOptionId(payment, installments),
      ...(payment.typeMethod !== EnumPaymentMethod.PIX && {
        card: buildCardData(payment),
      }),
    };

    if (
      !services.run_three_ds ||
      payment.typeMethod === EnumPaymentMethod.PIX
    ) {
      await mutateCreateCharge({
        id_charge: payment.chargeId!,
        payload: payloadCharge,
        id_establishment: userIdWhitelabel,
        token,
      });
      setLoading(false);
      return;
    }

    toast.update(TOAST_3DS_ID, { render: EnumStatus3DS.PAYMENT_INITIATED });

    try {
      payloadCharge.card = {
        ...payloadCharge.card,
        three_ds: await authenticateThreeDS(payment),
      } as CreateChargeRequestDTO["card"];
    } catch (error: any) {
      toast.update(TOAST_3DS_ID, {
        render: error?.message || "Houve um erro ao iniciar pagamento, tente novamente!",
        autoClose: TOAST_3DS_AUTO_CLOSE,
        type: "error",
        isLoading: false,
      });
      setStatusMethod(EnumStatusPaymentType.ERROR, payment.index);
      setLoading(false);
      return;
    }

    await mutateCreateCharge({
      payload: payloadCharge,
      id_charge: payment.chargeId!,
      id_establishment: userIdWhitelabel,
      browserInfoHeaders: getBrowserInfoHeaders(),
      token,
    });

    setLoading(false);
  }

  async function createCheckout({
    newToken,
    onSuccess,
  }: {
    newToken?: string;
    onSuccess?: () => void;
  }) {
    const token = isEnvironmentTest() ? X_TOKEN_AUTOMATION : newToken || tokenRecaptcha;
    const isSinglePayment = paymentMethods.length === 1;

    try {
      if (isSinglePayment) {
        const toastText =
          services.run_three_ds &&
            paymentMethods[0].typeMethod !== EnumPaymentMethod.PIX
            ? EnumStatus3DS.STARTED
            : EnumStatusPayment.STARTED;

        toast.loading(toastText, { toastId: TOAST_3DS_ID });
      }

      if (!token) return;

      setLoading(true);

      const charges = paymentMethods.map((method, index) => {
        return {
          id_charge_option: getChargeOptionId(
            { ...method, index },
            installments
          ),
          ...(method.typeMethod !== EnumPaymentMethod.PIX && {
            card: buildCardData(method) as Card,
          }),
        };
      });

      if (
        isSinglePayment &&
        services.run_three_ds &&
        paymentMethods[0].typeMethod !== EnumPaymentMethod.PIX
      ) {
        toast.update(TOAST_3DS_ID, { render: EnumStatus3DS.PAYMENT_INITIATED });

        const { public_key } = await PublicBffService.getPublicKey(userIdWhitelabel);
        const parcelamos3DS = new ParcelamosTudo3DSV2(public_key);
        const cardMethod = paymentMethods.find(isCard)!;

        const cardData = buildCardData(cardMethod);

        const executeReq = {
          amount: Math.round(getChargeOptionAmount({ ...paymentMethods[0], index: 0 }, installments) * 100),
          currency: "BRL",
          card: {
            number: cardData.number.replace(/\s/g, ""),
            exp_month: cardData.exp_month,
            exp_year: normalizeCardYear(cardData.exp_year),
          },
          user: {
            document: client.document?.replace(/\D/g, ""),
            email: client.email,
            first_name: client.name.split(" ")[0],
            last_name: client.name.split(" ").slice(1).join(" "),
            phone_number: client.phone?.replace(/\D/g, ""),
            address: client.address?.address,
            city: client.address?.city,
            state: client.address?.state,
            postal_code: client.address?.zip_code?.replace(/\D/g, ""),
            country: "BR",
          },
        };

        const result = await parcelamos3DS.execute(executeReq);

        if (!result.success) {
          throw new Error(result.message || "Erro na autenticação 3DS");
        }

        const threeDs: ThreeDs = {
          id: result.id_three_ds,
          provider: "CYBERSOURCE",
        };

        charges[0].card = {
          ...charges[0].card,
          three_ds: await authenticateThreeDS({ ...paymentMethods[0], index: 0 }),
        } as Card;
      }

      const payloadCheckout: CreateCheckoutOrderRequest = {
        id_client: client.id_client,
        charges,
        ...(rulesFromQuery.incentivate_id && {
          id_user: rulesFromQuery.incentivate_id,
        }),
      } as CreateCheckoutOrderRequest;

      const browserInfoHeaders = getBrowserInfoHeaders();

      await mutateCreateCheckout({
          payload: payloadCheckout,
          id_establishment: userIdWhitelabel,
          browserInfoHeaders,
          token: token as string,
        }).then((res) => {
          if (!isSinglePayment) {
            onSuccess?.();
          }
          return res;
        });

      const isPix = paymentMethods[0].typeMethod === EnumPaymentMethod.PIX;
      const isThreeDSDisabled = !services.run_three_ds;

      if (
        isSinglePayment &&
        (isPix || isThreeDSDisabled)
      ) {
        toast.update(TOAST_3DS_ID, {
          render: isPix
            ? EnumStatusPayment.PIX_PAYMENT
            : EnumStatusPayment.CHARGE_SUCCESS,
          autoClose: TOAST_3DS_AUTO_CLOSE,
          type: "success",
          isLoading: false,
        });

        setTimeout(() => {
          navigate(`/checkout/${idCheckout.current}`);
        }, TOAST_3DS_AUTO_CLOSE + 500);
      }

      if (
        isSinglePayment &&
        services.run_three_ds &&
        paymentMethods[0].typeMethod !== EnumPaymentMethod.PIX
      ) {
        toast.update(TOAST_3DS_ID, {
          render: EnumStatusPayment.CHARGE_SUCCESS,
          autoClose: TOAST_3DS_AUTO_CLOSE,
          type: "success",
          isLoading: false,
        });

        setTimeout(() => {
          navigate(`/checkout/${idCheckout.current}`);
        }, TOAST_3DS_AUTO_CLOSE + 500);
      }
    } catch (error: any) {
      if (error.name !== "AxiosError") {
        const message = "Houve um erro ao iniciar pagamento, tente novamente!";
        toast.update(TOAST_3DS_ID, {
          render: message,
          autoClose: TOAST_3DS_AUTO_CLOSE,
          type: "error",
          isLoading: false,
        });

        console.error(error);
      }
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, TOAST_3DS_AUTO_CLOSE);
    }
  }

  useEffect(() => {
    if (paymentMethods.every((method) => method.status === "success")) {
      setTimeout(() => {
        navigate(`/checkout/${idCheckout.current}`);
      }, TOAST_3DS_AUTO_CLOSE + 500);
    }
  }, [JSON.stringify(paymentMethods)]);

  return {
    createCheckout,
    createCharge,
    isLoading,
  };
}
